import { AxiosError, AxiosResponse } from 'axios';
import { makeAutoObservable } from 'mobx';
import { ErrorResponse, FileStateEnum, ParsedCadesCertificate, SignTypeEnum, StatementModel } from '../../models';
import { DataStore } from '../DataStore';
import cades from 'cryptopro-cadesplugin-gnivc';
import { UIStore } from '../UIStore';
import { ModalTypeEnum } from './ModalLibrary';

export class StatementPageLibrary
{
	private dataStore: DataStore;
	private uiStore: UIStore;

	constructor(dataStore: DataStore, uiStore: UIStore)
	{
		this.dataStore = dataStore;
		this.uiStore = uiStore;
		makeAutoObservable(this,
			{
				GetSubject:false
			}, { autoBind: true });
	}

	/** Состояние загрузки формы заявления */
	public FormFileState: FileStateEnum = FileStateEnum.Succeed;
	public setFormFileState(value: FileStateEnum) { this.FormFileState = value; }

	/** Состояние загрузки подписи */
	public SignFileState: FileStateEnum = FileStateEnum.Succeed;
	public setSignFileState(value: FileStateEnum) { this.SignFileState = value; }

	/** Ошибка загрузки формы заявления */
	public FormFileError: string = "";
	public setFormFileError(value: string) { this.FormFileError = value; }

	/** Ошибка загрузки подписи */
	public SignFileError: string = "";
	public setSignFileError(value: string) { this.SignFileError = value; }

	/** Тип подписи - через браузер или приложить файл */
	public SignType: SignTypeEnum = SignTypeEnum.None;
	setSignType(value: SignTypeEnum) { this.SignType = value; }

	/** Файл подписи */
	public DownloadingSignFile: File | null = null;
	setDownloadingSignFile(value: File | null) { this.DownloadingSignFile = value; }

	/** Состояние загрузки подписи */
	public DownloadingSignFileState: FileStateEnum = FileStateEnum.None;
	public setDownloadingSignFileState(value: FileStateEnum) { this.DownloadingSignFileState = value; }

	/** Ошибка загрузки подписи */
	public DownloadingSignFileError: string = "";
	public setDownloadingSignFileError(value: string) { this.DownloadingSignFileError = value; }

	/** Процент загрузки подписи */
	public DownloadingSignPercent: number = 0;
	public setDownloadingSignPercent(value: number) { this.DownloadingSignPercent = value; }

	public CertififcateListLoading: boolean = true;
	public setCertififcateListLoading(value: boolean) { this.CertififcateListLoading = value; }

	public CertificateList: ParsedCadesCertificate[] = [];
	public setCertificateList(value: ParsedCadesCertificate[]) { this.CertificateList = value; }

	public CertififcateListLoadingError: string = "";
	public setCertififcateListLoadingError(value: string) { this.CertififcateListLoadingError = value; }

	public CertificateSelectedThumbprint: string = "";
	public setCertificateSelectedThumbprint(value: string) { this.CertificateSelectedThumbprint = value; }

	public CertificateSelectedAlgorithm: string = "";
	public setCertificateSelectedAlgorithm(value: string) { this.CertificateSelectedAlgorithm = value; }


	public DeleteForm()
	{
		this.setFormFileState(FileStateEnum.Waiting);
		this.setFormFileError("");
		this.dataStore.DeleteForm()
			// в случае успешного сохранения
			.then((res: AxiosResponse<StatementModel>) =>
			{
				// если заявка не приехала, то ругаемся про неуспешное сохранение
				if (!res.data)
				{
					this.setFormFileState(FileStateEnum.Faulted);
					this.setFormFileError("Невозможно прочитать ответ сервера");
					return;
				}
				// записываем приехавший объект
				this.dataStore.setStatement(res.data);
				// парсим приехавший объект
				this.dataStore.UpdateSatementFromServer(false);
			})
			// в случае неуспешного сохранения
			.catch((res: AxiosError) =>
			{
				// выключаем спиннер
				this.setFormFileState(FileStateEnum.Faulted);

				// Ругаемся
				if (res.response?.data)
				{
					let error = (res.response.data as ErrorResponse);
					this.setFormFileError(error.MessageToUser + " (" + error.ActivityId + ")");
					return;
				}
				else this.setFormFileError("Ошибка удаления");
			});
	}

	public DeleteSign()
	{
		this.setSignFileState(FileStateEnum.Waiting);
		this.setSignFileError("");
		this.dataStore.DeleteSgnFile()
			// в случае успешного сохранения
			.then((res: AxiosResponse<StatementModel>) =>
			{
				// если заявка не приехала, то ругаемся про неуспешное сохранение
				if (!res.data)
				{
					this.setSignFileState(FileStateEnum.Faulted);
					this.setSignFileError("Невозможно прочитать ответ сервера");
					return;
				}
				// записываем приехавший объект
				this.dataStore.setStatement(res.data);
				// парсим приехавший объект
				this.dataStore.UpdateSatementFromServer(false);

				this.setDownloadingSignFile(null);
				this.setDownloadingSignFileState(FileStateEnum.None);
				this.setDownloadingSignFileError("");
				this.setSignFileState(FileStateEnum.Started);
				this.setSignFileError("");
			})
			// в случае неуспешного сохранения
			.catch((res: AxiosError) =>
			{
				// выключаем спиннер
				this.setSignFileState(FileStateEnum.Faulted);

				// Ругаемся
				if (res.response?.data)
				{
					let error = (res.response.data as ErrorResponse);
					this.setSignFileError(error.MessageToUser + " (" + error.ActivityId + ")");
					return;
				}
				else this.setSignFileError("Ошибка удаления");
			});
	}

	public UploadSign(data: Blob)
	{
		this.setDownloadingSignPercent(0);
		this.setDownloadingSignFileState(FileStateEnum.Waiting);
		this.setDownloadingSignFileError("");
		this.dataStore.UploadSgnFile(data)
			// в случае успешного
			.then((res: AxiosResponse<StatementModel>) =>
			{
				// если заявка не приехала, то ругаемся про неуспешное сохранение
				if (!res.data)
				{
					this.setDownloadingSignFileState(FileStateEnum.Faulted);
					this.setDownloadingSignFileError("Невозможно прочитать ответ сервера");
					return;
				}
				// записываем приехавший объект
				this.dataStore.setStatement(res.data);
				// парсим приехавший объект
				this.dataStore.UpdateSatementFromServer(false);
				// обновляем статусы подписи
				this.setDownloadingSignPercent(100);
				this.setDownloadingSignFileState(FileStateEnum.Succeed);
				this.setDownloadingSignFileError("");

				this.setSignFileState(FileStateEnum.Succeed);
				this.setSignFileError("");

			})
			// в случае неуспешного
			.catch((res: AxiosError) =>
			{
				// выключаем спиннер
				this.setDownloadingSignFileState(FileStateEnum.Faulted);

				// Ругаемся
				if (res.response?.data)
				{
					let error = (res.response.data as ErrorResponse);
					this.setDownloadingSignFileError(error.MessageToUser + " (" + error.ActivityId + ")");
					return;
				}
				else this.setDownloadingSignFileError("Ошибка удаления");

				this.setSignFileState(FileStateEnum.Faulted);
				this.setSignFileError(this.DownloadingSignFileError);
			});
	}

	public GetCertificateList()
	{
		this.setCertififcateListLoading(true);

		let list: ParsedCadesCertificate[] = [];
		this.setCertificateList([]);
		this.setCertififcateListLoadingError("");
		this.setCertificateSelectedThumbprint("");

		cades.getCertificateList(
			(certNum) =>
			{
				// start
				if (certNum === 0) this.setCertififcateListLoadingError("Сертификаты не обнаружены");
			},
			(certObj) =>
			{
				// iterate item
				list.push(new ParsedCadesCertificate(certObj));
			},
			() =>
			{
				// end
				this.setCertificateList(list);
				this.setCertififcateListLoading(false);
			},
			(stage, exc, pluginInfo) =>
			{
				// error
				this.setCertififcateListLoading(false);
				this.setCertificateList([]);
				this.setCertififcateListLoadingError(stage + " " + exc.message);
			}
		);

	}

	public CreateSign()
	{
		if (!this.dataStore.Statement?.Form?.Hash256 || !this.dataStore.Statement?.Form?.Hash512 || !this.CertificateSelectedThumbprint || !this.CertificateSelectedAlgorithm)
		{
			this.uiStore.modal.setShowModal(true, ModalTypeEnum.Error, "Выбор сертификата", "Произошла ошибка при использовании сертификата");
			return;
		}
		if (this.dataStore.Statement.Form.Hash256.length === 0 || this.dataStore.Statement.Form.Hash512.length === 0 || this.CertificateSelectedThumbprint.length === 0 || this.CertificateSelectedAlgorithm.length === 0)
		{
			this.uiStore.modal.setShowModal(true, ModalTypeEnum.Error, "Выбор сертификата", "Произошла ошибка при использовании сертификата");
			return;
		}
		this.uiStore.modal.setShowModal(true, ModalTypeEnum.Spinner, "Подпись заявления");

		let hash: string;
		if (this.CertificateSelectedAlgorithm === "1.2.643.7.1.1.1.1") hash = this.dataStore.Statement.Form.Hash256;
		else hash = this.dataStore.Statement.Form.Hash512;

		cades.signData(
			hash,
			this.CertificateSelectedThumbprint,
			this.CertificateSelectedAlgorithm,
			(algorithm:string, signatureAsBase64: string, versionPlugin: string, versionCSP: string, nameCSP: string) =>
			{
				this.dataStore.UploadSgn(algorithm, signatureAsBase64, versionPlugin, versionCSP, nameCSP)
					// в случае успешного
					.then((res: AxiosResponse<StatementModel>) =>
					{
						// если заявка не приехала, то ругаемся про неуспешное сохранение
						if (!res.data)
						{
							this.uiStore.modal.setShowModal(true, ModalTypeEnum.Error, "Подпись заявления", "Невозможно прочитать ответ сервера");
							return;
						}
						// помечаем что подпись в норме
						this.setSignFileState(FileStateEnum.Succeed);
						this.setSignFileError("");
						// записываем приехавший объект
						this.dataStore.setStatement(res.data);
						// парсим приехавший объект
						this.dataStore.UpdateSatementFromServer(false);
						// Убираем модалку
						this.uiStore.modal.setShowModal(false);
					})
					// в случае неуспешного
					.catch((res: AxiosError) =>
					{
						// Убираем модалку
						this.uiStore.modal.setShowModal(false);

						// Ругаемся
						if (res.response?.data)
						{
							let error = (res.response.data as ErrorResponse);
							this.uiStore.modal.setShowModal(true, ModalTypeEnum.Error, "Ошибка сертификата", error.MessageToUser + " (" + error.ActivityId + ")");
							return;
						}
						else this.uiStore.modal.setShowModal(true, ModalTypeEnum.Error, "Подпись заявления", "Ошибка сохранения подписи");
					});
			},
			(stage, exc, pluginInfo) =>
			{
				// error
				this.uiStore.modal.setShowModal(true, ModalTypeEnum.Error, "Подпись заявления", stage + " " + exc.message);
			}
		);

	}

	public GetSubject(val: Map<string, string>)
	{
		let sn = val.get("SN");
		let g = val.get("G");
		let cn = val.get("CN");
		let fio = "";
		if (sn) fio = sn;
		if (g)
		{
			if (fio.length > 0) fio += " ";
			fio += g;
		}
		fio = fio.trim();
		if (fio.length > 0) return fio;
		return cn;
	}

}
